import { useCallback, useState } from 'react';
import { ActionType, ComponentType, logEvent, markStep } from '@cbhq/client-analytics';

import { rumAddAction } from '../../utils/analytics/datadogRum';
import { logError } from '../../utils/logging';

import type { MemberRole } from './utils/constants';
import type { LegalEntityRole } from './useMultiLEProductSelection';
import { useTeamApi } from './useTeamApi';

type UseInviteSubmissionParams = {
  organizationId: string;
  email: string;
  selectedRoles: MemberRole[];
  legalEntityIds: string[];
  legalEntityRoles?: LegalEntityRole[];
  onSuccess: () => void;
  onFailure: (error: unknown) => void;
};

type UseInviteSubmissionReturn = {
  isSubmitting: boolean;
  submitInvite: () => Promise<void>;
};

export function useInviteSubmission({
  organizationId,
  email,
  selectedRoles,
  legalEntityIds,
  legalEntityRoles,
  onSuccess,
  onFailure,
}: UseInviteSubmissionParams): UseInviteSubmissionReturn {
  const { inviteMember } = useTeamApi(organizationId);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitInvite = useCallback(async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const variant = legalEntityRoles?.length ? 'multi_le' : 'single_le';

    try {
      markStep('submit_workspace_member_invite');
      logEvent('member_invite_submitted', {
        componentType: ComponentType.button,
        action: ActionType.click,
      });

      await inviteMember({
        email,
        roles: selectedRoles,
        legalEntityIds,
        legalEntityRoles,
      });

      markStep('workspace_member_invite_success');
      logEvent('member_invite_success', {
        componentType: ComponentType.api,
        action: ActionType.api,
      });
      rumAddAction('member_invite_success', { variant });
      setIsSubmitting(false);
      onSuccess();
    } catch (error) {
      logEvent('member_invite_failed', {
        componentType: ComponentType.api,
        action: ActionType.api,
      });
      rumAddAction('member_invite_failed', { variant });
      logError(error, {
        context: 'useInviteSubmission.submitInvite',
        isHandled: true,
        isUserVisible: true,
      });
      setIsSubmitting(false);
      onFailure(error);
    }
  }, [
    email,
    inviteMember,
    isSubmitting,
    legalEntityIds,
    legalEntityRoles,
    onFailure,
    onSuccess,
    selectedRoles,
  ]);

  return {
    isSubmitting,
    submitInvite,
  };
}
